import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Database,
  Leaf,
  TrendingUp,
  Truck,
  Zap,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Lock,
  ShieldCheck,
  Globe2,
  Server,
} from 'lucide-react';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import { Language } from '@/types';

interface SolutionsPageProps {
  lang: Language;
}

export const SolutionsPage: React.FC<SolutionsPageProps> = ({ lang }) => {
  const isRtl = lang === 'ar';
  const navigate = useNavigate();
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);

  // ─── Translation Dictionary ─────────────────────────────────────────────────
  const t = {
    en: {
      seoTitle: "VEXL Solutions for Business — Enterprise Spatial AI",
      seoDesc: "Explore proven ways to deploy advanced GeoAI and spatial intelligence across corporate departments, critical industries, and field operations.",
      badge: "VEXL ENTERPRISE SOLUTIONS",
      heroTitle: "Put spatial AI to work across your organization",
      heroSubtitle: "Explore proven ways to deploy advanced GeoAI and spatial intelligence across your operational teams—engineered with deterministic geodetics, zero-egress security, and field-tested accuracy.",
      ctaPrimary: "Explore VEXL Nova",
      ctaSecondary: "Contact Systems Team",
      
      // Departments
      deptHeader: "OPERATIONAL DEPARTMENTS",
      deptSubtitle: "Deterministic spatial workflows for every enterprise unit.",
      depts: [
        {
          id: "finance",
          code: "SYS-01 // RISK",
          title: "Finance & Risk",
          desc: "Align capital planning with real-world spatial exposure. Calculate asset vulnerability and parametric climate risk indices deterministically.",
          detail: "Regional risk scoring APIs",
          icon: <BarChart3 size={20} />,
        },
        {
          id: "analytics",
          code: "SYS-02 // DATA",
          title: "Data Analytics",
          desc: "Transform multi-spectral satellite rasters and complex vector boundaries into verified territorial insights and anomaly signals.",
          detail: "Calibrated radiometric ingest",
          icon: <Database size={20} />,
        },
        {
          id: "operations",
          code: "SYS-03 // AGRI",
          title: "Operations & Agri",
          desc: "Model parcel-level soil moisture dynamics, crop phenology, and yield distributions across regional production basins.",
          detail: "Field-scale NDVI & NDWI tracking",
          icon: <Leaf size={20} />,
        },
        {
          id: "sales",
          code: "SYS-04 // TRADE",
          title: "Sales & Commodity Trading",
          desc: "Anticipate regional harvest volumes, commodity throughput, and supply chain constraints ahead of published market consensus.",
          detail: "Basin harvest forecast models",
          icon: <TrendingUp size={20} />,
        },
        {
          id: "logistics",
          code: "SYS-05 // LOGISTICS",
          title: "Logistics & Supply Chains",
          desc: "Optimize transportation corridors and asset routing using real-time terrain traversability and meteorological risk envelopes.",
          detail: "Geodesic network routing",
          icon: <Truck size={20} />,
        },
        {
          id: "engineering",
          code: "SYS-06 // CORE",
          title: "Engineering & Infrastructure",
          desc: "Deploy automated spatial pipelines and Large Spatial Models (LSMs) with sub-second execution times via VEXL autonomous agents.",
          detail: "Sovereign SDK & GeoParquet APIs",
          icon: <Zap size={20} />,
        },
      ],

      // Capabilities
      useCaseHeader: "TECHNICAL CAPABILITIES",
      useCases: [
        {
          title: "Predictive Intelligence",
          desc: "Detect drought vectors and crop moisture stress 3 to 6 months before ground-level evidence emerges using multi-sensor radar and optical fusion.",
          metric: "UNEP & NARSS Validated",
        },
        {
          title: "Autonomous Agent Systems",
          desc: "Automate complex spatial analysis, buffer operations, and script generation through the VEXL Nova deterministic reasoning runtime.",
          metric: "Sub-Second Invariant Execution",
        },
        {
          title: "Parametric Underwriting",
          desc: "Index agricultural insurance triggers directly to verified satellite band telemetry with mathematically proven zero-dispute audit trails.",
          metric: "Deterministic Settlement Logic",
        },
        {
          title: "Geospatial Data Engineering",
          desc: "Stream and process petabytes of Earth observation data into high-performance vector and raster tiles without positional degradation.",
          metric: "Zero-Latency Spatial Pipeline",
        },
      ],

      // Industries
      indHeader: "INDUSTRIES SERVED",
      indSubtitle: "Purpose-built spatial intelligence for mission-critical sectors.",
      industries: [
        {
          id: "agri",
          title: "Agriculture & Commodity Trading",
          desc: "Predict regional yield variations and mitigate severe weather risks at parcel scale with area-weighted spectral indices.",
          tag: "AgroScore Engine",
        },
        {
          id: "insurance",
          title: "Parametric & Crop Insurance",
          desc: "Audit agricultural damage claims and establish index-based coverage triggers using certified remote sensing rasters.",
          tag: "CropLens Auditor",
        },
        {
          id: "government",
          title: "Governments & Sovereign Institutions",
          desc: "Monitor national food security, aquifer depletion, and territorial resources via air-gapped early warning sentinels.",
          tag: "DroughtWatch Sentinel",
        },
        {
          id: "infrastructure",
          title: "Utilities & Infrastructure",
          desc: "Safeguard high-voltage transmission lines, pipeline corridors, and transport networks with automated change detection.",
          tag: "Infrastructure Sentinel",
        },
      ],

      // Security
      secHeader: "Built-in Enterprise Data Sovereignty",
      secDesc: "Protect your proprietary spatial pipelines. VEXL maintains complete data isolation: your coordinates, farm geometries, and business contexts remain encrypted, air-gapped, and strictly segregated from external model training.",
      secBadge1: "GDPR & Sovereign Data Compliant",
      secBadge2: "AES-256 GCM Encrypted at Rest",
      secBadge3: "Isolated Agent Sandboxing",
      secBadge4: "Zero Public Model Training",
    },
    ar: {
      seoTitle: "حلول VEXL للمؤسسات وقطاع الأعمال — الذكاء المكاني المتقدم",
      seoDesc: "استكشف طرقاً مثبتة لنشر أنظمة الذكاء الاصطناعي الجيومكاني عبر إدارات المؤسسات، والقطاعات الحيوية، والعمليات الحقلية.",
      badge: "حلول VEXL للمؤسسات",
      heroTitle: "ضع الذكاء الاصطناعي الجغرافي في قلب مؤسستك",
      heroSubtitle: "استكشف طرقاً مثبتة لتطبيق أنظمة الذكاء المكاني المتقدمة عبر فرق العمل لديك—مبنية بحسابات جيوديسية حتمية، وأمان سيادي مانع للتسريب، ودقة حقلية معتمدة.",
      ctaPrimary: "استكشف VEXL Nova",
      ctaSecondary: "تواصل مع فريق النظم",
      
      // Departments
      deptHeader: "الأقسام والإدارات التشغيلية",
      deptSubtitle: "مسارات عمل مكانية حتمية لكل وحدة تشغيلية داخل المؤسسة.",
      depts: [
        {
          id: "finance",
          code: "SYS-01 // المخاطر",
          title: "المالية والمخاطر",
          desc: "ربط تخطيط رأس المال بالتعرض المكاني الحقيقي. حساب مؤشرات مخاطر الأصول والمخاطر المناخية البارامترية بشكل حتمي.",
          detail: "واجهات تقييم المخاطر الإقليمية",
          icon: <BarChart3 size={20} />,
        },
        {
          id: "analytics",
          code: "SYS-02 // البيانات",
          title: "تحليل البيانات المكانية",
          desc: "تحويل مصفوفات الأقمار متعددة الأطياف وحدود المتجهات المعقدة إلى رؤى إقليمية موثقة وإشارات شذوذ فورية.",
          detail: "معالجة إشعاعية معايرة",
          icon: <Database size={20} />,
        },
        {
          id: "operations",
          code: "SYS-03 // الزراعة",
          title: "العمليات والزراعة",
          desc: "نمذجة ديناميكيات رطوبة التربة الحقلية، ومراحل نمو المحاصيل، وتوزيعات الإنتاجية عبر أحواض الإنتاج الإقليمية.",
          detail: "تتبع مؤشرات NDVI و NDWI الحقلية",
          icon: <Leaf size={20} />,
        },
        {
          id: "sales",
          code: "SYS-04 // التجارة",
          title: "المبيعات وتجارة السلع",
          desc: "استشراف كميات الحصاد الإقليمية، وتدفق السلع، واختناقات سلاسل الإمداد قبل صدور التقديرات السوقية المعتادة.",
          detail: "نماذج توقع إنتاجية الأحواض",
          icon: <TrendingUp size={20} />,
        },
        {
          id: "logistics",
          code: "SYS-05 // اللوجستيات",
          title: "اللوجستيات وسلاسل الإمداد",
          desc: "تحسين ممرات النقل وتوجيه الأصول عبر تقييمات فورية لقابلية اجتياز التضاريس والظروف المناخية المحيطة.",
          detail: "توجيه الشبكات الجيوديسية",
          icon: <Truck size={20} />,
        },
        {
          id: "engineering",
          code: "SYS-06 // البنية",
          title: "الهندسة والبنية التحتية",
          desc: "نشر خطوط معالجة مكانية مؤتمتة ونماذج مساحية كبرى (LSMs) بزمن استجابة أقل من ثانية عبر وكلاء VEXL المستقلين.",
          detail: "واجهات برمجية بصيغة GeoParquet",
          icon: <Zap size={20} />,
        },
      ],

      // Capabilities
      useCaseHeader: "القدرات الفنية",
      useCases: [
        {
          title: "الذكاء التنبؤي",
          desc: "الكشف عن مؤشرات الجفاف والإجهاد المائي للمحاصيل قبل 3 إلى 6 أشهر من ظهور الأدلة الميدانية عبر دمج بيانات الرادار والأطياف البصرية.",
          metric: "معتمد من الأمم المتحدة وهيئة الاستشعار",
        },
        {
          title: "أنظمة الوكلاء الذاتية",
          desc: "أتمتة التحليلات المكانية المعقدة، وحسابات النطاقات الجغرافية، وتوليد الأكواد عبر بيئة الاستدلال الحتمي لمحرك VEXL Nova.",
          metric: "تنفيذ فوري خاضع للثوابت الهندسية",
        },
        {
          title: "التأمين الزراعي البارامتري",
          desc: "ربط مشغلات وثائق التأمين الزراعي مباشرة بقياسات أطياف الأقمار الصناعية الموثقة بمسار تدقيق خالٍ من النزاعات.",
          metric: "منطق تسويات حتمي مؤتمت بالكامل",
        },
        {
          title: "هندسة البيانات المكانية",
          desc: "بث ومعالجة بيتابايت من بيانات مراقبة الأرض إلى بلاط متجهات ومصفوفات عالية الأداء دون فقدان الدقة الموضعية.",
          metric: "خطوط معالجة مكانية بلا تأخير",
        },
      ],

      // Industries
      indHeader: "القطاعات المخدومة",
      indSubtitle: "ذكاء مكاني مصمم خصيصاً للقطاعات الحيوية وصناع القرار.",
      industries: [
        {
          id: "agri",
          title: "الزراعة وتجارة المحاصيل الكبرى",
          desc: "التنبؤ بتفاوت الإنتاج الإقليمي والتحوط من مخاطر الطقس الحادة على مستوى القطع الحقلية بمؤشرات طيفية مرجحة بالمساحة.",
          tag: "محرك AgroScore",
        },
        {
          id: "insurance",
          title: "التأمين الزراعي البارامتري",
          desc: "تدقيق مطالبات أضرار المحاصيل وتفعيل التغطيات التأمينية المؤشرية باستخدام مصفوفات الاستشعار عن بعد المعتمدة.",
          tag: "مدقق CropLens",
        },
        {
          id: "government",
          title: "الحكومات والمؤسسات السيادية",
          desc: "مراقبة منظومة الأمن الغذائي القومي، واستنزاف الأحواض الجوفية، والموارد الإقليمية بأنظمة إنذار مبكر معزولة شبكياً.",
          tag: "مرصد DroughtWatch",
        },
        {
          id: "infrastructure",
          title: "المرافق والبنية التحتية",
          desc: "حماية خطوط نقل الطاقة عالية الجهد، وممرات أنابيب النفط والغاز، وشبكات النقل عبر كشف التغيرات المكانية المؤتمت.",
          tag: "مرصد البنية التحتية",
        },
      ],

      // Security
      secHeader: "سيادة وأمان البيانات مكفول للمؤسسات",
      secDesc: "احمِ قنوات البيانات المكانية الخاصة بك. تضمن VEXL عزلاً تاماً للبيانات: إحداثياتك، ومضلعات حقولك، وسياقات أعمالك تظل مشفرة، ومعزولة شبكياً، ومستبعدة تماماً من تدريب أي نماذج خارجية.",
      secBadge1: "متوافق مع معايير حماية البيانات السيادية و GDPR",
      secBadge2: "تشفير كامل AES-256 GCM في حالة السكون والنقل",
      secBadge3: "بيئات تشغيل معزولة للوكلاء الأذكياء",
      secBadge4: "انعدام تام لاستخدام البيانات لتدريب النماذج العامة",
    },
  }[lang];

  return (
    <PageTransition>
      <SEO
        title={t.seoTitle}
        description={t.seoDesc}
        lang={lang}
      />

      <div className={`min-h-screen bg-[#05070A] text-[#EDF0F1] pt-24 md:pt-28 pb-24 relative overflow-hidden ${isRtl ? 'font-arabic' : 'font-sans'}`} dir={isRtl ? 'rtl' : 'ltr'}>
        <RasterGridBackdrop type="type-a" />

        <div className="max-w-[1400px] mx-auto px-6 space-y-24 relative z-10">
          
          {/* ══════ HERO SECTION (Editorial Split) ══════ */}
          <section className="border-b border-[#262C31] pb-16">
            <div
              className={`
                w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start
                ${isRtl ? 'text-right' : 'text-left'}
              `}
            >
              {/* Left Column (Headline) */}
              <div className="lg:col-span-7 space-y-3">
                <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-semibold mb-3">
                  {t.badge}
                </div>
                <h1
                  className="text-3xl md:text-5xl lg:text-[54px] font-medium text-[#EDF0F1] tracking-[-0.5px] max-w-2xl leading-[1.12]"
                  style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
                >
                  {t.heroTitle}
                </h1>
              </div>

              {/* Right Column (Sub-Description & CTAs) */}
              <div className="lg:col-span-5 space-y-6 lg:pt-6">
                <p className="text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] max-w-lg font-normal">
                  {t.heroSubtitle}
                </p>
                <div className="flex items-center gap-3 pt-2 flex-wrap">
                  <button
                    onClick={() => navigate('/products/nova')}
                    className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0E93A3] hover:bg-[#075C68] text-white font-semibold text-xs tracking-wide shadow-[0_0_15px_rgba(14,147,163,0.3)] transition-all duration-200"
                  >
                    <span>{t.ctaPrimary}</span>
                    <ArrowRight size={13} className={`group-hover:translate-x-0.5 transition-transform ${isRtl ? 'rotate-180' : ''}`} />
                  </button>
                  <button
                    onClick={() => navigate('/contact')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3] text-[#EDF0F1] font-medium text-xs tracking-wide transition-all duration-200"
                  >
                    <span>{t.ctaSecondary}</span>
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* ══════ DEPARTMENTS SECTION ══════ */}
          <div className="space-y-10">
            <div className="space-y-2">
              <span className="text-[11px] font-mono font-bold uppercase tracking-[0.22em] text-[#0E93A3]">
                {t.deptHeader}
              </span>
              <p
                className="text-2xl md:text-3xl font-medium tracking-tight text-[#EDF0F1]"
                style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
              >
                {t.deptSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {t.depts.map((dept) => (
                <div
                  key={dept.id}
                  onMouseEnter={() => setHoveredSection(dept.id)}
                  onMouseLeave={() => setHoveredSection(null)}
                  onClick={() => navigate(`/solutions`)}
                  className="
                    group relative rounded-xl p-6 md:p-8
                    border border-[#262C31] bg-[#12161A] hover:border-[#0E93A3]/50
                    transition-all duration-200 cursor-pointer overflow-hidden shadow-sm
                  "
                >
                  <div className="relative z-10 flex flex-col justify-between h-full space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="w-10 h-10 rounded-lg bg-[#05070A] border border-[#262C31] flex items-center justify-center text-[#0E93A3] group-hover:border-[#0E93A3]/40 group-hover:bg-[#0E93A3]/10 transition-colors">
                          {dept.icon}
                        </div>
                        <span className="text-[10px] font-mono text-[#66707A] group-hover:text-[#9AA5AC] transition-colors tnum">
                          {dept.code}
                        </span>
                      </div>

                      <h3 className="text-lg font-semibold text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors">
                        {dept.title}
                      </h3>
                      <p className="text-[#9AA5AC] text-xs md:text-sm leading-relaxed font-normal">
                        {dept.desc}
                      </p>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-[#262C31]">
                      <span className="text-[10px] font-mono text-[#66707A] group-hover:text-[#0E93A3] uppercase tracking-wider transition-colors">
                        {dept.detail}
                      </span>
                      <ArrowUpRight size={14} className="text-[#66707A] group-hover:text-[#0E93A3] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ══════ USE CASES / CAPABILITIES SECTION ══════ */}
          <div className="py-16 border-y border-[#262C31]">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-4 space-y-4 pr-0 lg:pr-8">
                <span className="text-[11px] font-mono font-bold uppercase tracking-[0.22em] text-[#0E93A3]">
                  {t.useCaseHeader}
                </span>
                <h3
                  className="text-2xl md:text-3xl font-medium text-[#EDF0F1] leading-tight"
                  style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
                >
                  {lang === 'ar' ? 'حلول حقلية مصممة للنمو والأمان' : 'Engineered for real-world impact and strict geodetic precision.'}
                </h3>
              </div>
              <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                {t.useCases.map((uc, idx) => (
                  <div key={idx} className="space-y-3 p-5 rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3]/40 transition-colors">
                    <span className="text-xs font-mono font-bold text-[#EDF0F1] uppercase tracking-wider block">
                      {uc.title}
                    </span>
                    <p className="text-[#9AA5AC] text-xs leading-relaxed">
                      {uc.desc}
                    </p>
                    <div className="pt-2 border-t border-[#262C31] flex items-center gap-2">
                      <CheckCircle2 size={12} className="text-[#0E93A3]" />
                      <span className="text-[10px] font-mono text-[#0E93A3] uppercase tracking-wider tnum">{uc.metric}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ══════ INDUSTRIES SECTION ══════ */}
          <div className="space-y-10">
            <div className="space-y-2">
              <span className="text-[11px] font-mono font-bold uppercase tracking-[0.22em] text-[#0E93A3]">
                {t.indHeader}
              </span>
              <p
                className="text-2xl md:text-3xl font-medium tracking-tight text-[#EDF0F1]"
                style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
              >
                {t.indSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {t.industries.map((ind) => (
                <div
                  key={ind.id}
                  onClick={() => navigate(`/solutions`)}
                  className="
                    group relative rounded-xl p-8
                    border border-[#262C31] bg-[#12161A] hover:border-[#0E93A3]/50
                    transition-all duration-200 cursor-pointer overflow-hidden shadow-sm
                  "
                >
                  <div className="relative z-10 flex flex-col justify-between h-full space-y-6">
                    <div className="space-y-4">
                      <span className="text-[10px] font-mono font-bold uppercase tracking-[0.15em] text-[#0E93A3] px-3 py-1 rounded-full bg-[#0E93A3]/10 border border-[#0E93A3]/25">
                        {ind.tag}
                      </span>
                      <h3 className="text-xl font-bold text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors pt-2">
                        {ind.title}
                      </h3>
                      <p className="text-[#9AA5AC] text-sm leading-relaxed">
                        {ind.desc}
                      </p>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-[#262C31]">
                      <span className="text-xs font-medium text-[#66707A] group-hover:text-[#9AA5AC] transition-colors">
                        {lang === 'ar' ? 'عرض تفاصيل الحل' : 'View solution details'}
                      </span>
                      <div className="w-8 h-8 rounded-full bg-[#05070A] border border-[#262C31] flex items-center justify-center text-[#9AA5AC] group-hover:border-[#0E93A3] group-hover:text-[#0E93A3] transition-all">
                        <ArrowRight size={14} className={isRtl ? 'rotate-180' : ''} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ══════ SECURITY BANNER ══════ */}
          <div className="relative rounded-xl border border-[#262C31] bg-[#12161A] overflow-hidden p-8 md:p-12 lg:p-14">
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
              <div className="lg:col-span-3 space-y-6">
                <div className="w-12 h-12 rounded-lg bg-[#05070A] border border-[#262C31] flex items-center justify-center text-[#0E93A3]">
                  <Lock size={22} />
                </div>
                <h3
                  className="text-2xl md:text-3xl lg:text-4xl font-medium text-[#EDF0F1] leading-tight"
                  style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
                >
                  {t.secHeader}
                </h3>
                <p className="text-[#9AA5AC] text-sm md:text-base leading-relaxed">
                  {t.secDesc}
                </p>
                <div className="flex items-center gap-2.5 flex-wrap pt-2">
                  {[t.secBadge1, t.secBadge2, t.secBadge3, t.secBadge4].map((b, idx) => (
                    <span key={idx} className="text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 rounded-full bg-[#05070A] border border-[#262C31] text-[#EDF0F1]">
                      {b}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="lg:col-span-2 flex justify-center">
                <div className="p-6 rounded-xl bg-[#05070A] border border-[#262C31] shadow-xl max-w-sm w-full space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-[#262C31]">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#0E93A3]" />
                      <span className="text-[10px] font-mono text-[#9AA5AC] uppercase">VEXL SPATIAL TUNNEL</span>
                    </div>
                    <span className="text-[9px] font-mono text-[#0E93A3] font-bold">SECURE // ZERO-TRUST</span>
                  </div>
                  <div className="space-y-2">
                    <div className="p-3 rounded-lg bg-[#12161A] border border-[#262C31] flex items-center justify-between">
                      <span className="text-xs font-mono text-[#EDF0F1]">Zero-Trust Auth & MTLS</span>
                      <CheckCircle2 size={14} className="text-[#0E93A3]" />
                    </div>
                    <div className="p-3 rounded-lg bg-[#12161A] border border-[#262C31] flex items-center justify-between">
                      <span className="text-xs font-mono text-[#EDF0F1]">Isolated Vector Sandboxing</span>
                      <CheckCircle2 size={14} className="text-[#0E93A3]" />
                    </div>
                    <div className="p-3 rounded-lg bg-[#12161A] border border-[#262C31] flex items-center justify-between">
                      <span className="text-xs font-mono text-[#EDF0F1]">AES-256 GCM In-Flight/At-Rest</span>
                      <CheckCircle2 size={14} className="text-[#0E93A3]" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </PageTransition>
  );
};

export default SolutionsPage;
